import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, Subject, catchError, finalize, takeUntil, tap } from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { CompanyInvoiceDetails } from 'src/app/core/models/sales/company-invoice-details.interface';
import {
  canMarkInvoicePaid,
  getInvoiceStatusLabelKey,
  InvoiceStatus,
  normalizeInvoiceStatus,
} from 'src/app/core/models/sales/invoice-status.type';
import { InvoiceErrorBody, InvoiceService } from 'src/app/core/services/invoice.service';

interface InvoiceModalLineView {
  id: string;
  description: string;
  quantity: number;
  unitOfMeasureId: number;
  unitPrice: number;
  taxPercent: number;
  amountExcludingTax: number;
  totalTaxAmount: number;
  amountIncludingTax: number;
}

interface InvoiceModalHeaderView {
  number: string;
  invoiceDate: string;
  dueDate: string;
  status: InvoiceStatus;
  statusLabelKey: string;
  statusBadgeClass: string;
  customerName: string;
  customerId: string;
  salesOrderNumber: string;
  salesOrderId: string | null;
  subtotal: number;
  taxTotal: number;
  total: number;
  // Precomputed footer gates so the template binds to VM fields, not method calls.
  canMarkPaid: boolean;
  canDelete: boolean;
}

@Component({
  selector: 'app-invoice-modal',
  standalone: true,
  imports: [CommonModule, CoreModule, TranslateModule],
  // ViewEncapsulation.None lets the @media print rules below scope to the document body so the
  // host shell (sidebar, topbar) is hidden during print without using ::ng-deep. The rules are
  // namespaced under .invoice-modal so they cannot leak into other pages.
  encapsulation: ViewEncapsulation.None,
  templateUrl: './invoice-modal.component.html',
  styleUrl: './invoice-modal.component.scss',
})
export class InvoiceModalComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) public companyId = '';
  @Input() public invoiceId: string | null = null;
  @Input() public isOpen = false;

  @Output() public readonly closed = new EventEmitter<void>();
  @Output() public readonly paid = new EventEmitter<CompanyInvoiceDetails>();
  @Output() public readonly deleted = new EventEmitter<string>();

  public readonly invoice$ = new BehaviorSubject<CompanyInvoiceDetails | null>(null);
  public readonly headerView$ = new BehaviorSubject<InvoiceModalHeaderView | null>(null);
  public readonly lineViews$ = new BehaviorSubject<InvoiceModalLineView[]>([]);

  public isLoading = false;
  public isMutating = false;
  public pageErrorKey = '';
  public actionErrorKey = '';

  public isMarkPaidPromptOpen = false;
  public isDeletePromptOpen = false;

  // Map structured backend error codes to i18n keys for the in-modal error banner.
  // Keep in sync with TaskManager.Services.InvoiceErrorCodes.
  private readonly errorCodeKeyMap: Readonly<Record<string, string>> = {
    INVOICE_INVALID_REQUEST: 'COMPONENTS.INVOICES.ERRORS.INVOICE_INVALID_REQUEST',
    INVOICE_INVALID_STATUS: 'COMPONENTS.INVOICES.ERRORS.INVOICE_INVALID_STATUS',
    INVOICE_TERMINAL: 'COMPONENTS.INVOICES.ERRORS.INVOICE_TERMINAL',
    INVOICE_NO_PRICE: 'COMPONENTS.INVOICES.ERRORS.INVOICE_NO_PRICE',
    INVOICE_NO_CUSTOMER: 'COMPONENTS.INVOICES.ERRORS.INVOICE_NO_CUSTOMER',
    ORDER_TERMINAL: 'COMPONENTS.INVOICES.ERRORS.ORDER_TERMINAL',
  };

  private readonly unsubscribe$ = new Subject<void>();
  private loadedInvoiceId: string | null = null;

  private readonly invoiceService = inject(InvoiceService);

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] || changes['invoiceId'] || changes['companyId']) {
      this.maybeLoad();
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public retry(): void {
    this.loadedInvoiceId = null;
    this.maybeLoad();
  }

  public requestClose(): void {
    if (this.isMutating) {
      return;
    }
    this.closed.emit();
  }

  public print(): void {
    window.print();
  }

  public openMarkPaidPrompt(): void {
    if (this.isMutating) {
      return;
    }
    this.actionErrorKey = '';
    this.isMarkPaidPromptOpen = true;
  }

  public closeMarkPaidPrompt(): void {
    if (this.isMutating) {
      return;
    }
    this.isMarkPaidPromptOpen = false;
  }

  public confirmMarkPaid(): void {
    if (this.isMutating || !this.companyId || !this.invoiceId) {
      return;
    }
    this.isMutating = true;
    this.actionErrorKey = '';

    this.invoiceService
      .markCompanyInvoicePaid(this.companyId, this.invoiceId)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((invoice) => {
          this.applyInvoice(invoice);
          this.isMarkPaidPromptOpen = false;
          this.paid.emit(invoice);
        }),
        catchError((error: HttpErrorResponse) => {
          this.actionErrorKey = this.mapErrorKey(error, 'COMPONENTS.INVOICES.ERRORS.UPDATE_FAILED');
          return EMPTY;
        }),
        finalize(() => {
          this.isMutating = false;
        }),
      )
      .subscribe();
  }

  public openDeletePrompt(): void {
    if (this.isMutating) {
      return;
    }
    this.actionErrorKey = '';
    this.isDeletePromptOpen = true;
  }

  public closeDeletePrompt(): void {
    if (this.isMutating) {
      return;
    }
    this.isDeletePromptOpen = false;
  }

  public confirmDelete(): void {
    if (this.isMutating || !this.companyId || !this.invoiceId) {
      return;
    }
    const invoiceId = this.invoiceId;
    this.isMutating = true;
    this.actionErrorKey = '';

    this.invoiceService
      .deleteCompanyInvoice(this.companyId, invoiceId)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(() => {
          this.isDeletePromptOpen = false;
          this.deleted.emit(invoiceId);
        }),
        catchError((error: HttpErrorResponse) => {
          this.actionErrorKey = this.mapErrorKey(error, 'COMPONENTS.INVOICES.ERRORS.DELETE_FAILED');
          return EMPTY;
        }),
        finalize(() => {
          this.isMutating = false;
        }),
      )
      .subscribe();
  }

  private maybeLoad(): void {
    if (!this.isOpen) {
      this.loadedInvoiceId = null;
      this.actionErrorKey = '';
      this.isMarkPaidPromptOpen = false;
      this.isDeletePromptOpen = false;
      return;
    }

    if (!this.companyId || !this.invoiceId) {
      return;
    }

    if (this.loadedInvoiceId === this.invoiceId) {
      return;
    }

    this.loadInvoice();
  }

  private loadInvoice(): void {
    if (!this.companyId || !this.invoiceId) {
      return;
    }

    const invoiceId = this.invoiceId;
    this.isLoading = true;
    this.pageErrorKey = '';
    this.invoice$.next(null);
    this.headerView$.next(null);
    this.lineViews$.next([]);

    this.invoiceService
      .getCompanyInvoiceById(this.companyId, invoiceId)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((invoice) => {
          this.loadedInvoiceId = invoiceId;
          this.applyInvoice(invoice);
        }),
        catchError(() => {
          this.pageErrorKey = 'COMPONENTS.INVOICES.ERRORS.LOAD_DETAILS_FAILED';
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  private applyInvoice(invoice: CompanyInvoiceDetails): void {
    this.invoice$.next(invoice);
    const status = normalizeInvoiceStatus(invoice.status);
    this.headerView$.next({
      number: invoice.number,
      invoiceDate: invoice.invoiceDate,
      dueDate: invoice.dueDate,
      status,
      statusLabelKey: getInvoiceStatusLabelKey(status),
      statusBadgeClass: InvoiceModalComponent.statusBadgeClass(status),
      // Always display the human-readable customer name; never the raw customerId. The detail
      // endpoint populates `customerName`; if the BE somehow returns an empty value, show a
      // dash placeholder rather than leaking an opaque id into the UI.
      customerName: invoice.customerName?.trim() || '—',
      customerId: invoice.customerId,
      salesOrderNumber: invoice.salesOrderNumber?.trim() || '',
      salesOrderId: invoice.salesOrderId,
      subtotal: invoice.subtotal,
      taxTotal: invoice.taxTotal,
      total: invoice.total,
      canMarkPaid: canMarkInvoicePaid(status),
      canDelete: status === 'Open',
    });
    // All numeric fields below are persisted snapshots — never recompute on the client.
    this.lineViews$.next(
      invoice.lines.map((line) => ({
        id: line.id,
        description: line.description ?? '',
        quantity: line.quantity,
        unitOfMeasureId: line.unitOfMeasureId,
        unitPrice: line.unitPrice,
        taxPercent: line.taxPercent,
        amountExcludingTax: line.amountExcludingTax,
        totalTaxAmount: line.totalTaxAmount,
        amountIncludingTax: line.amountIncludingTax,
      })),
    );
  }

  private mapErrorKey(error: HttpErrorResponse, fallbackKey: string): string {
    const code = (error.error as InvoiceErrorBody | null)?.code;
    if (code && this.errorCodeKeyMap[code]) {
      return this.errorCodeKeyMap[code];
    }
    return fallbackKey;
  }

  private static statusBadgeClass(status: InvoiceStatus): string {
    return status === 'Paid'
      ? 'invoice-modal__status-badge--paid'
      : 'invoice-modal__status-badge--open';
  }
}
